import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Popper, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';

export type CoachmarkPlacement = 'bottom-start' | 'top-start' | 'right';

export interface CoachmarkState {
  label: string;
  preview: React.ReactNode;
}

export interface FieldCoachmarkProps {
  title: string;
  description: string;
  preview?: React.ReactNode;
  states?: CoachmarkState[];
  placement?: CoachmarkPlacement;
  linkLabel?: string;
  onLinkClick?: () => void;
  descriptionId?: string;
  triggerAriaLabel?: string;
  sx?: SxProps<Theme>;
}

const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 200;
const BRIDGE_PX = 10;

const visuallyHidden: SxProps<Theme> = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const FieldCoachmark = (props: FieldCoachmarkProps) => {
  const {
    title,
    description,
    preview,
    states,
    placement = 'bottom-start',
    linkLabel,
    onLinkClick,
    descriptionId,
    triggerAriaLabel,
    sx,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressFocusOpen = useRef(false);
  const suppressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [stateIndex, setStateIndex] = useState(0);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const clearSuppressTimer = useCallback(() => {
    if (suppressTimer.current) clearTimeout(suppressTimer.current);
    suppressTimer.current = null;
  }, []);

  const close = useCallback(
    (returnFocus = false) => {
      clearTimers();
      setOpen(false);
      if (!returnFocus) return;
      suppressFocusOpen.current = true;
      triggerRef.current?.focus();
      clearSuppressTimer();
      suppressTimer.current = setTimeout(() => {
        suppressFocusOpen.current = false;
      }, 0);
    },
    [clearTimers, clearSuppressTimer],
  );

  const scheduleOpen = useCallback(() => {
    if (isCoarsePointer) return;
    clearTimers();
    openTimer.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [clearTimers, isCoarsePointer]);

  const scheduleClose = useCallback(() => {
    if (isCoarsePointer) return;
    clearTimers();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimers, isCoarsePointer]);

  useEffect(
    () => () => {
      clearTimers();
      clearSuppressTimer();
    },
    [clearTimers, clearSuppressTimer],
  );

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (popperRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close]);

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearTimers();
    setOpen(current => !current);
  };

  const handleTriggerFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
    if (suppressFocusOpen.current) {
      suppressFocusOpen.current = false;
      return;
    }
    let keyboardFocus = false;
    try {
      keyboardFocus = event.target.matches(':focus-visible');
    } catch {
      keyboardFocus = false;
    }
    if (keyboardFocus) {
      clearTimers();
      setOpen(true);
    }
  };

  const handleTriggerBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && popperRef.current?.contains(next)) return;
    close();
  };

  const activeStateIndex = states && states.length > 0 ? Math.min(stateIndex, states.length - 1) : 0;
  const activePreview = states && states.length > 0 ? states[activeStateIndex].preview : preview;

  const accessibleId = descriptionId ?? undefined;
  const balloonId = `${descriptionId ?? 'field-coachmark'}-balloon`;

  const balloonBackground = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const bodyColor = isDark ? theme.palette.text.secondary : theme.palette.grey[700];

  const transition = useMemo(
    () =>
      reducedMotion
        ? 'none'
        : theme.transitions.create(['opacity', 'transform'], {
            duration: 150,
            easing: theme.transitions.easing.easeOut,
          }),
    [reducedMotion, theme],
  );

  return (
    <>
      {accessibleId && (
        <Box component="span" id={accessibleId} sx={visuallyHidden}>
          {`${title}. ${description}`}
        </Box>
      )}

      <Box
        component="span"
        sx={[{ display: 'inline-flex', alignItems: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      >
        <ButtonBase
          ref={triggerRef}
          type="button"
          aria-label={triggerAriaLabel ?? `Ajuda sobre ${title}`}
          aria-expanded={open}
          aria-describedby={open ? balloonId : undefined}
          onClick={handleTriggerClick}
          onMouseDown={event => event.stopPropagation()}
          onFocus={handleTriggerFocus}
          onBlur={handleTriggerBlur}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            color: bodyColor,
            transition: reducedMotion ? 'none' : theme.transitions.create(['color', 'background-color']),
            ...(open
              ? { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.12) }
              : null),
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 15 }} />
        </ButtonBase>
      </Box>

      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement={placement}
        role={isCoarsePointer ? 'dialog' : 'tooltip'}
        aria-label={isCoarsePointer ? title : undefined}
        modifiers={[
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', enabled: true, options: { padding: 8 } },
        ]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ placement: resolvedPlacement }) => {
          const side = resolvedPlacement.split('-')[0];
          const bridge =
            side === 'top'
              ? { paddingBottom: `${BRIDGE_PX}px` }
              : side === 'left'
              ? { paddingRight: `${BRIDGE_PX}px` }
              : side === 'right'
              ? { paddingLeft: `${BRIDGE_PX}px` }
              : { paddingTop: `${BRIDGE_PX}px` };
          const offset =
            side === 'top'
              ? 'translateY(4px)'
              : side === 'left'
              ? 'translateX(4px)'
              : side === 'right'
              ? 'translateX(-4px)'
              : 'translateY(-4px)';
          return (
            <Box
              ref={popperRef}
              sx={bridge}
              onMouseEnter={() => {
                if (!isCoarsePointer) clearTimers();
              }}
              onMouseLeave={scheduleClose}
            >
              <Box
                id={balloonId}
                sx={{
                  position: 'relative',
                  width: 320,
                  maxWidth: 'calc(100vw - 32px)',
                  padding: 1.5,
                  borderRadius: '10px',
                  border: `0.5px solid ${theme.palette.divider}`,
                  backgroundColor: balloonBackground,
                  boxShadow: theme.shadows[8],
                  transition,
                  opacity: entered ? 1 : 0,
                  transform: entered ? 'none' : offset,
                }}
              >
                {isCoarsePointer && (
                  <ButtonBase
                    type="button"
                    aria-label="Fechar ajuda"
                    onClick={() => close(true)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      color: bodyColor,
                      '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </ButtonBase>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pr: isCoarsePointer ? 3 : 0 }}>
                  <MyLocationIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
                    {title}
                  </Typography>
                </Box>

                <Typography sx={{ mt: 0.75, fontSize: 11.5, lineHeight: 1.45, color: bodyColor, whiteSpace: 'pre-line' }}>
                  {description}
                </Typography>

                {states && states.length > 1 && (
                  <Box
                    role="tablist"
                    aria-label="Estados da configuração"
                    sx={{
                      display: 'inline-flex',
                      mt: 1.25,
                      padding: '2px',
                      borderRadius: '6px',
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    }}
                  >
                    {states.map((state, index) => {
                      const selected = index === activeStateIndex;
                      return (
                        <ButtonBase
                          key={state.label}
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          onClick={() => setStateIndex(index)}
                          sx={{
                            px: 1,
                            py: 0.25,
                            fontSize: 10.5,
                            fontWeight: selected ? 600 : 400,
                            borderRadius: '4px',
                            color: selected ? 'text.primary' : bodyColor,
                            backgroundColor: selected ? theme.palette.background.paper : 'transparent',
                            boxShadow: selected ? theme.shadows[1] : 'none',
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: 1,
                            },
                          }}
                        >
                          {state.label}
                        </ButtonBase>
                      );
                    })}
                  </Box>
                )}

                {activePreview && <Box sx={{ mt: 1.25 }}>{activePreview}</Box>}

                {(linkLabel || !isCoarsePointer) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mt: 1.25,
                    }}
                  >
                    {linkLabel ? (
                      <ButtonBase
                        type="button"
                        onClick={onLinkClick}
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'primary.main',
                          borderRadius: '4px',
                          '&:hover': { textDecoration: 'underline' },
                          '&:focus-visible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {linkLabel}
                      </ButtonBase>
                    ) : (
                      <span />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        }}
      </Popper>
    </>
  );
};

export default FieldCoachmark;
